// pages/master-data/set-driver/SetDriverPage.jsx
'use client';
import { useState, useEffect, useMemo } from 'react';
import DataTable from '../../../../components/DataTable';
import ActionMenu from '../../../../components/ActionMenu';
import { useNotifier } from '../../../../components/Notifier';
import DeleteConfirmDialog from '../../../../components/DeleteConfirmDialog';
import SetDriverAddEdit from './SetDriverAddEdit';
import { useLanguage } from '../../../../utils/i18n';
import dayjs from 'dayjs';

import { useDriverAssignList, deleteDriverAssign } from '../../../../stores/driverAssignStore';
import { useCompanyList } from '../../../../stores/companyStore';
import Breadcrumbs from '../../../../components/@extended/Breadcrumbs';
import { fleetApi } from '../../../../api/axiosClient';

const formatDateShort = (val) => {
  if (!val) return '-';
  const d = dayjs(val);
  return d.isValid() ? d.format('DD-MM-YYYY') : '-';
};

function SetDriverPageContent() {
  const { t } = useLanguage();
  const notify = useNotifier();

  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
  const [searchField, setSearchField] = useState('driver_name');
  const [searchRaw, setSearchRaw] = useState('');
  const [searchValue, setSearchValue] = useState('');

  const [localRows, setLocalRows] = useState([]);
  const [localLoading, setLocalLoading] = useState(false);
  const [localTotal, setLocalTotal] = useState(0);

  const searchFieldOptions = useMemo(
    () => [
      { value: 'all', label: t('general.searchAll', 'All') },
      { value: 'driver_name', label: t('setDriver.driverName', 'Driver Name') },
      { value: 'vehicle_name', label: t('setDriver.vehicleName', 'Vehicle Name') },
      { value: 'license_plate', label: t('setDriver.licensePlate', 'License Plate') },
      { value: 'valid_at', label: t('setDriver.validAt', 'Valid At') },
      { value: 'exp_at', label: t('setDriver.expiredAt', 'Expired At') }
    ],
    [t]
  );

  const [openDialog, setOpenDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTargetId, setConfirmTargetId] = useState(null);
  const [confirmName, setConfirmName] = useState('');
  const [confirmLoading, setConfirmLoading] = useState(false);

  const { companyOptions } = useCompanyList();
  const [selectedCompanies, setSelectedCompanies] = useState(() => {
    try {
      const stored = typeof window !== 'undefined' ? localStorage.getItem('companyId') : null;
      return stored ? [{ value: String(stored), label: String(stored) }] : [];
    } catch {
      return [];
    }
  });

  const companyOptionsNormalized = useMemo(
    () =>
      (companyOptions || []).map((c) => ({
        value: String(c.value ?? c.id ?? c),
        label: c.label ?? c.name ?? String(c.value ?? c.id ?? c)
      })),
    [companyOptions]
  );

  useEffect(() => {
    try {
      if (!companyOptionsNormalized || companyOptionsNormalized.length === 0) return;
      const raw = typeof window !== 'undefined' ? localStorage.getItem('user') : null;
      if (!raw) return;
      let u = null;
      try {
        u = JSON.parse(raw);
      } catch {
        return;
      }
      const companyId = u?.company ?? u?.company_id ?? null;
      if (companyId == null) return;
      const companyIdStr = String(companyId);
      const match = companyOptionsNormalized.find((o) => String(o.value) === companyIdStr);
      const selected = match
        ? [{ value: String(match.value), label: match.label ?? String(match.value) }]
        : [{ value: companyIdStr, label: companyIdStr }];
      setSelectedCompanies((prev) => {
        const prevVal = prev && prev[0] && String(prev[0].value);
        if (prevVal === selected[0].value) return prev;
        return selected;
      });
    } catch (e) {
      console.error('Auto select company failed', e);
    }
  }, [companyOptionsNormalized]);

  const filterCompanyIds = useMemo(() => {
    if (!selectedCompanies || selectedCompanies.length === 0) return '';
    const hasAll = selectedCompanies.some((s) => String(s.value) === 'all');
    if (hasAll) return '';
    return selectedCompanies.map((s) => String(s.value)).join(',');
  }, [selectedCompanies]);

  const hookSearchField = searchField === 'all' ? '' : searchField;
  const hookSearchValue = searchField === 'all' ? '' : searchValue;

  const {
    data: rowsFromHook,
    isLoading: hookLoading,
    totalRecord: hookTotal
  } = useDriverAssignList(paginationModel.page, paginationModel.pageSize, hookSearchField, hookSearchValue, filterCompanyIds);

  const rows = searchField === 'all' ? localRows : rowsFromHook || [];
  const isLoading = searchField === 'all' ? localLoading : hookLoading;
  const totalRecord = searchField === 'all' ? localTotal : hookTotal;

  useEffect(() => {
    if (searchField !== 'all') return;
    const controller = new AbortController();

    const fetchAllSearch = async () => {
      setLocalLoading(true);
      try {
        const start = paginationModel.page * paginationModel.pageSize;
        const length = paginationModel.pageSize;
        const columns = [{ logic_operator: 'like', name: 'id', operator: 'AND', value: '' }];
        let filters = [];

        if (searchValue) {
          filters = [
            { name: 'driver_name', value: searchValue },
            { name: 'vehicle_name', value: searchValue },
            { name: 'license_plate', value: searchValue },
            { name: 'valid_at', value: searchValue },
            { name: 'exp_at', value: searchValue }
          ];
        }

        const payload = {
          paging: { start, length },
          orders: { columns: ['id'], ascending: false },
          joins: [
            { name: 'company', column_results: ['name'] },
            { name: 'driver', column_results: ['name'] },
            { name: 'vehicle', column_results: ['license_plate', 'name'] }
          ],
          columns: columns,
          filters: filters
        };

        if (filterCompanyIds) {
          if (filterCompanyIds.includes(',')) {
            payload.columns.push({ logic_operator: 'in', name: 'company_id', operator: 'AND', value: filterCompanyIds });
          } else {
            payload.columns.push({ logic_operator: '=', name: 'company_id', operator: 'AND', value: filterCompanyIds });
          }
        } else {
          try {
            const stored = localStorage.getItem('companyId');
            const companyId = stored == null || stored === '' ? null : isNaN(Number(stored)) ? stored : Number(stored);
            if (companyId != null) {
              payload.columns.push({ logic_operator: '=', name: 'company_id', operator: 'AND', value: String(companyId) });
            }
          } catch {}
        }

        const tz = `?tz=${Math.abs(new Date().getTimezoneOffset() / 60)}`;
        const resp = await fleetApi.post(`/driver-assign-vehicle/search${tz}`, payload, { signal: controller.signal });
        const data = resp?.data ?? null;

        const fetchedRows = Array.isArray(data) ? data : (data?.data ?? []);
        const total = Array.isArray(data) ? fetchedRows.length : (data?.totalRecord ?? data?.totalRecords ?? data?.dataSize ?? 0);

        setLocalRows(fetchedRows);
        setLocalTotal(total);
      } catch (err) {
        if (err.name !== 'AbortError') console.error('driver assign search all error', err);
      } finally {
        if (!controller.signal.aborted) setLocalLoading(false);
      }
    };

    fetchAllSearch();
    return () => controller.abort();
  }, [searchField, searchValue, paginationModel, filterCompanyIds]);

  useEffect(() => {
    const t = setTimeout(() => {
      setSearchValue(searchRaw);
      setPaginationModel((p) => ({ ...p, page: 0 }));
    }, 700);
    return () => clearTimeout(t);
  }, [searchRaw]);

  const handlePageChange = (model) => {
    setPaginationModel((prev) => {
      if (prev.page === model.page && prev.pageSize === model.pageSize) return prev;
      return model;
    });
  };

  const handleSearch = ({ field, value }) => {
    setSearchField(field);
    setSearchRaw(value ?? '');
  };

  const handleCompanyChange = (options = []) => {
    const sanitized = (options || []).map((o) => ({ value: String(o.value), label: o.label ?? String(o.value) }));
    setSelectedCompanies(sanitized);
    try {
      if (typeof window !== 'undefined') {
        if (sanitized.length === 1) localStorage.setItem('companyId', String(sanitized[0].value));
        else localStorage.removeItem('companyId');
      }
    } catch {
      console.log('Error saving companyId');
    }
  };

  const handleAddClick = () => {
    setEditingId(null);
    setOpenDialog(true);
  };

  const handleEditClick = (id) => {
    setEditingId(id);
    setOpenDialog(true);
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setEditingId(null);
  };

  const handleDelete = (id, itemName) => {
    setConfirmTargetId(id);
    setConfirmName(itemName || '');
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!confirmTargetId) return;
    setConfirmLoading(true);
    try {
      await deleteDriverAssign(confirmTargetId);
      notify({ message: t('masterDriver.notifSucces', 'Driver successfully deleted'), severity: 'success' });
      setPaginationModel((p) => ({ ...p }));
    } catch (err) {
      console.error(err);
      notify({ message: t('masterDriver.notifFailed', 'Failed to delete Driver'), severity: 'error' });
    } finally {
      setConfirmLoading(false);
      setConfirmOpen(false);
      setConfirmTargetId(null);
      setConfirmName('');
    }
  };
  const currentUserRole = typeof window !== 'undefined' ? localStorage.getItem('rolename') || localStorage.getItem('role_name') || '' : '';

  const columns = [
    // {
    //   field: 'company_name',
    //   headerName: t('general.company', 'Company'),
    //   flex: 1,
    //   valueGetter: (params, row) => row?.join?.company_name || '-'
    // },
    {
      field: 'driver_name',
      headerName: t('setDriver.driverName', 'Driver Name'),
      flex: 1,
      renderCell: (params) => params.row?.join?.driver_name || params.row?.driver_name || '-'
    },
    {
      field: 'vehicle_name',
      headerName: t('setDriver.vehicle', 'Vehicle'),
      flex: 1,
      renderCell: (params) => params.row?.join?.vehicle_license_plate || params.row?.license_plate || '-'
    },
    {
      field: 'license_plate',
      headerName: t('setDriver.licensePlate', 'License Plate'),
      flex: 1,
      renderCell: (params) => params.row?.join?.license_plate || params.row?.license_plate || '-'
    },
    {
      field: 'valid_at',
      headerName: t('setDriver.validAt', 'Valid At'),
      flex: 1,
      renderCell: (params) => formatDateShort(params.row.valid_at)
    },
    {
      field: 'exp_at',
      headerName: t('setDriver.expiredAt', 'Expired At'),
      flex: 1,
      renderCell: (params) => formatDateShort(params.row.exp_at)
    },

    ...(currentUserRole === 'Administrator'
      ? [
          { field: 'created_by', headerName: t('setDriver.createdBy', 'Created By'), flex: 0.5, hide: true },
          { field: 'created_date', headerName: t('setDriver.createdDate', 'Created Date'), flex: 0.5, hide: true },
          { field: 'modified_by', headerName: t('setDriver.modifiedBy', 'Modified By'), flex: 0.5, hide: true },
          { field: 'modified_date', headerName: t('setDriver.modifiedDate', 'Modified Date'), flex: 0.5, hide: true }
        ]
      : []),
    {
      field: 'actions',
      headerName: t('general.actions', 'Actions'),
      sortable: false,
      width: 120,
      renderCell: (params) => <ActionMenu row={params.row} onEdit={handleEditClick} onDelete={handleDelete} mode="inline" />
    }
  ];

  return (
    <div>
      <DataTable
        title={t('setDriver.assignmentTitle', 'Assignment')}
        rows={rows || []}
        columns={columns}
        loading={isLoading}
        pageSizeOptions={[10, 25, 50]}
        paginationMode="server"
        rowCount={totalRecord || 0}
        paginationModel={paginationModel}
        onPaginationModelChange={handlePageChange}
        searchQuery={searchRaw}
        onSearch={handleSearch}
        searchFieldOptions={searchFieldOptions}
        selectedSearchField={searchField}
        onSearchFieldChange={(f) => {
          setSearchField(f);
          setSearchRaw('');
          setSearchValue('');
          setPaginationModel((p) => ({ ...p, page: 0 }));
        }}
        onAddClick={handleAddClick}
        searchLabel={t('general.search', 'Search')}
        companyOptions={companyOptionsNormalized}
        selectedCompanies={selectedCompanies}
        onCompanyChange={handleCompanyChange}
      />

      <SetDriverAddEdit
        open={openDialog}
        id={editingId}
        companyOptions={companyOptionsNormalized}
        onClose={handleDialogClose}
        onSuccess={() => {
          setPaginationModel((p) => ({ ...p }));
          handleDialogClose();
        }}
      />

      <DeleteConfirmDialog
        open={confirmOpen}
        loading={confirmLoading}
        onClose={() => setConfirmOpen(false)}
        itemName={confirmName}
        onConfirm={handleConfirmDelete}
        title={t('deleteConfirm.title', 'Confirm Delete')}
        message={t('setDriver.confirmDeleteMessage', 'Are you sure you want to remove this assignment?')}
        confirmLabel={t('deleteConfirm.label', 'Delete')}
        cancelLabel={t('deleteConfirm.cancel', 'Cancel')}
      />
    </div>
  );
}

export default function SetDriverPage() {
  const { t } = useLanguage();
  return (
    <>
      <Breadcrumbs
        custom
        heading={t('setDriver.settingDriver', 'Setting Driver')}
        links={[
          { title: t('setDriver.tracking', 'Tracking'), to: '#' },
          { title: t('setDriver.driver', 'Driver'), to: '/master-driver?name=driver' },
          { title: t('setDriver.settingDriver', 'Setting Driver') }
        ]}
      />
      <SetDriverPageContent />
    </>
  );
}
